// LAP-766 — Agrégation des sources Life du conseiller écologie.
//
// Même pattern que `fetchTimeAdvisorSources` : on isole chaque endpoint
// (catch indépendant) pour qu'un endpoint absent ne fasse pas tomber tout
// l'agrégat. `buildEcoAdvisor` produit alors un diagnostic partiel mais
// exploitable.
//
// Source principale : /api/Dashboard/reports/ qui renvoie un tableau de cartes
// typées. On en extrait :
//   - type "globalemission" → empreinte annuelle (logement/transport/…),
//   - type "conseils"       → dernière recommandation Claude persistée.
import { makeGetRequest } from 'utils/api'

/**
 * Normalise une réponse API en tableau, quelle que soit l'enveloppe.
 */
export function extractList(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.results)) return payload.results
  if (Array.isArray(payload.data)) return payload.data
  return []
}

/**
 * Récupère et démêle les sources nécessaires à `buildEcoAdvisor`.
 *
 * @param {object} fallbackUser - profil Redux à utiliser si /api/user/user/
 *   échoue.
 * @returns {Promise<object>} { user, globalEmission, advice }
 */
export async function fetchEcoAdvisorSources(fallbackUser = {}) {
  const safeGet = async (path) => {
    try {
      const r = await makeGetRequest(path, {}, true)
      return r?.data ?? null
    } catch (_) {
      return null
    }
  }

  const [userData, reports] = await Promise.all([
    safeGet('/api/user/user/'),
    safeGet('/api/Dashboard/reports/'),
  ])

  const cards = extractList(reports)
  const globalCard = cards.find((c) => c?.type === 'globalemission')
  const conseilsCard = cards.find((c) => c?.type === 'conseils')

  return {
    user: userData || fallbackUser || {},
    // `data` de la carte globalemission = tableau par année.
    globalEmission: globalCard ? extractList(globalCard.data) : [],
    // `data` de la carte conseils = dernière recommandation (objet) ou {}.
    advice: conseilsCard?.data || {},
  }
}

/**
 * LAP-766 — Récupère l'historique réel des recommandations écologie
 * (DisplayedAdvice module="Ecology"). Mirroir du conseiller temps.
 *
 * @returns {Promise<Array>} liste de recommandations (peut être vide).
 */
export async function fetchEcoAdviceHistory() {
  try {
    const r = await makeGetRequest(
      '/api/Dashboard/reports/?history=true',
      {},
      true,
    )
    return extractList(r?.data)
  } catch (_) {
    return []
  }
}
